import { AddToListButton } from '@app/components/AddToListButton';
import { ArtistInfoBadges } from '@app/components/ArtistBadges';
import { LocalisableLink } from '@app/components/LocalisableLink';
import { ProductCard } from '@app/components/ProductCard';
import { useListsInclusions } from '@app/hooks/useListsInclusions';
import {
  parseRecommendedInventoryItemToAnalyticsEcommerceItem,
  trackGA4ViewItemListEvent,
} from '@app/pages/i/analytics';
import { FollowArtistButton } from '@app/pages/i/components/FollowArtistButton/FollowArtistButton';
import { PreviewStyle } from '@app/pages/i/constants';
import { useFollowArtist } from '@app/pages/i/hooks/useFollowArtist';
import { ProductPageProps } from '@app/pages/i/types';
import { getPreviewUrl } from '@app/utils/common';
import { getPriceComponent } from '@app/utils/getPriceComponent';
import { SearchUUIDStorageKey } from '@app/utils/searchUUIDStorage';
import {
  Box,
  Button,
  Carousel,
  constants,
  StoreIcon,
  Text,
} from '@redbubble/design-system';
import { useViewportWidth } from '@redbubble/next/hooks';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { AppProps, DeviceType, UserInfo } from '@redbubble/next/types';
import {
  type Item as AnalyticsEcommerceItem,
  AnalyticsItemsList,
} from '@redbubble/next-analytics/gtm';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import {
  RSProductClickedLocation,
  RSProductClickedPageType,
} from '@redbubble/next-provider/rudderstack';
import { memo, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import type { Artist, ListsButtonInventoryItem } from 'types/shop-bff/types';
import { AvatarStack } from '../AvatarStack';
import styles from './ArtistAttribution.module.scss';
import {
  ARTIST_ATTRIBUTION_LIMIT,
  ARTIST_ATTRIBUTION_SEO_LINK_LIMIT,
  DESKTOP_SLIDE_COUNT,
  MOBILE_SLIDE_COUNT,
} from './constants';
import {
  useArtistInventoryItemPortfolio,
  useArtistShopArtistInfo,
} from './utils';

const messages = defineMessages({
  moreByThisArtist: {
    defaultMessage: 'More by this artist',
  },
  designedAndSoldBy: {
    defaultMessage: 'Designed and sold by <link>{artistName}</link>',
  },
  worksCount: {
    defaultMessage: `{worksCount} {worksCount, plural,
      one {design}
      other {designs}
    }`,
  },
  viewShop: {
    defaultMessage: 'View shop',
  },
});

interface ArtistAttributionProps {
  inventoryItemId: string;
  artist: Artist;
}

export const ArtistAttribution: React.FC<ArtistAttributionProps> = memo(
  ({ inventoryItemId, artist }) => {
    const [{ locale, country, currency, isLoggedIn }] = useUserInfo<UserInfo>();
    const { isFollowing } = usePageProps<ProductPageProps & AppProps>();
    const { ref, inView } = useInView({ triggerOnce: true });
    const artistId = parseInt(artist.id, 10);

    const { followingArtist, handleOnToggleFollowArtist } = useFollowArtist({
      isLoggedIn,
      artistId,
      initialFollowingStatus: !!isFollowing?.isFollowed,
    });

    // TODO DEP-1846/1845: implement follow hook
    const {
      artistInventory,
      loading, // TODO: DEP-2385: Implement Skeleton
      refresh: refreshArtistInventory,
    } = useArtistInventoryItemPortfolio();

    // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
    useEffect(() => {
      if (inView) {
        const items = (() => {
          if (artistInventory && artistInventory.results) {
            return artistInventory.results
              .map((artistInventoryItem, index) => {
                if (!artistInventoryItem) return null;
                return parseRecommendedInventoryItemToAnalyticsEcommerceItem({
                  recommendedInventoryItem: artistInventoryItem,
                  index,
                });
              })
              .filter(
                (
                  item: AnalyticsEcommerceItem | null
                ): item is NonNullable<AnalyticsEcommerceItem> => !!item
              );
          }
          return [];
        })();

        if (items && items.length > 0) {
          trackGA4ViewItemListEvent({
            items,
            listID: AnalyticsItemsList.ArtistAttributionCarousel,
            topic: '',
            searchUUIDStorageKey: SearchUUIDStorageKey.LocationPathname,
          });
        }
      }
    }, [inView, artistInventory, artist]);

    const {
      artistShopArtistInfo,
      // loading: loadingArtistInfo,
      refresh: refreshArtistShopArtistInfo,
    } = useArtistShopArtistInfo();

    const intl = useIntl();

    const { isOrAbove } = useViewportWidth();
    const isDesktop = isOrAbove(DeviceType.Desktop);

    const {
      username: artistUsername,
      avatar,
      shopDisplayName: artistName,
    } = artist;

    // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
    useEffect(() => {
      refreshArtistInventory({
        inventoryItemId,
        artistUsername,
        currency,
        country,
        limit: ARTIST_ATTRIBUTION_LIMIT,
        locale,
        previewTypeIds: [
          PreviewStyle.CLOSE_PREVIEW,
          PreviewStyle.ALTERNATE_PRODUCT_CLOSE,
        ],
      });
    }, [artistUsername, country, currency, locale, refreshArtistInventory]);

    useEffect(() => {
      refreshArtistShopArtistInfo({
        artistName: artist.username,
        locale,
      });
    }, [artist.username, locale, refreshArtistShopArtistInfo]);

    const { listsInclusions, refresh: refreshListInclusions } =
      useListsInclusions();

    useEffect(() => {
      if (!loading && artistInventory?.results) {
        refreshListInclusions(
          artistInventory.results.map((ai) => ai?.inventoryItem)
        );
      }
    }, [artistInventory?.results, loading, refreshListInclusions]);

    const worksCount = artistShopArtistInfo?.worksCount;

    if (!artistInventory?.results) return null;

    const slides = artistInventory.results
      .map((result, index) => {
        const item = result?.inventoryItem;
        if (!item) return null;

        const imageSrc = getPreviewUrl(
          item,
          result?.defaultPreviewTypeId ?? PreviewStyle.CLOSE_PREVIEW
        );

        const productPageUrl = item.productPageUrl;
        const volumeDiscountThresholds = item.volumeDiscount?.thresholds || [];

        const price = item.price;
        const workId = item.work.id;
        const title = item.work.title;
        const matureContent = item.work.matureContent;

        const productLink = productPageUrl
          ? new URL(productPageUrl).pathname
          : undefined;

        if (!productLink) return null;

        return (
          <ProductCard
            key={item.id}
            title={title}
            workTitle={title}
            link={productLink}
            artistName={artistName}
            imageSrc={imageSrc}
            matureContent={matureContent}
            loading={loading}
            analyticsIndex={index}
            hideLink={index >= ARTIST_ATTRIBUTION_SEO_LINK_LIMIT}
            analyticsComponentID={AnalyticsItemsList.ArtistAttributionCarousel}
            rsLocation={RSProductClickedLocation.ArtistAttributionCarousel}
            rsPageType={RSProductClickedPageType.Product}
            primaryAction={
              <AddToListButton
                inventoryItem={item as ListsButtonInventoryItem}
                workId={workId}
                listsInclusions={listsInclusions}
                analyticsComponentID={
                  AnalyticsItemsList.ArtistAttributionCarousel
                }
                analyticsIndex={index}
                searchUUIDStorageKey={SearchUUIDStorageKey.LocationPathname}
              />
            }
            stockStatus={item.stockStatus}
          >
            {getPriceComponent(volumeDiscountThresholds, price)}
          </ProductCard>
        );
      })
      .filter(Boolean);

    if (!slides.length) return null;

    const artistProfileUrl = artist.profileUrl;

    return (
      <Box className={styles.artistAttributionGridContainer} ref={ref}>
        <Box className={styles.avatarGridItem}>
          <AvatarStack avatar={avatar} href={artistProfileUrl}>
            <Box display="flex" flexDirection="column">
              <Text element="h2" type="display4" marginBottom="xxs">
                {intl.formatMessage(messages.designedAndSoldBy, {
                  link: LocalisableLink(artistProfileUrl),
                  artistName,
                })}
              </Text>
              <Box
                className={styles.artistInfoRow}
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
              >
                {!!worksCount && (
                  <Text type="body2" className={styles.worksCount}>
                    {intl.formatMessage(messages.worksCount, {
                      worksCount,
                    })}
                  </Text>
                )}

                {(artist.fanArtist || artist.featuredArtist) && (
                  <Box className={styles.badgeSeparator} />
                )}
                <ArtistInfoBadges
                  fanArtist={artist.fanArtist}
                  featuredArtist={artist.featuredArtist}
                />
              </Box>
            </Box>
          </AvatarStack>
        </Box>

        <Text
          className={styles.subHeadingGridItem}
          element="h2"
          type="display3"
        >
          {intl.formatMessage(messages.moreByThisArtist)}
        </Text>

        <Box className={styles.carouselGridItem}>
          <Box display="block" className={styles.carousel}>
            <Carousel
              profile={isDesktop ? constants.DESKTOP : constants.MOBILE}
              slidesPerView={
                isDesktop ? DESKTOP_SLIDE_COUNT : MOBILE_SLIDE_COUNT
              }
            >
              {slides}
            </Carousel>
          </Box>
        </Box>
        <Box className={styles.leftActionGridItem}>
          <FollowArtistButton
            followingArtist={followingArtist}
            handleOnToggleFollowArtist={handleOnToggleFollowArtist}
          />
        </Box>
        <Box className={styles.rightActionGridItem}>
          {!!artistProfileUrl && (
            <Button
              intent={constants.PRIMARY}
              size={constants.SMALL}
              iconBefore={<StoreIcon />}
              href={artistProfileUrl}
            >
              {intl.formatMessage(messages.viewShop)}
            </Button>
          )}
        </Box>
      </Box>
    );
  }
);
